import { CONST } from '@common/constants/const.constant';
import { MQ } from '@common/constants/mq.constant';
import { THEME } from '@common/constants/theme.constant';
import { EAddressType } from '@common/enums/address-type.enum';
import { FormAddressByEnum } from '@common/general-ui/address-by-enum/form-address-by-enum.component';
import { ButtonV2 } from '@common/general-ui/button-v2/button-v2.component';
import { FieldLabelV2 } from '@common/general-ui/field-v2/field-label-v2.component';
import { Form } from '@common/general-ui/form/form.component';
import { IInputLoqateValue } from '@common/general-ui/input-loqate/input-loqate.component';
import { StatusTagV2 } from '@common/general-ui/tag-v2/status-tag-v2.component';
import { IUseCustomFormReturn } from '@common/hooks/use-form.hook';
import { IRegisterModel } from '@src/api/model/register.model';
import { ENV } from '@src/shared/env.constant';
import { EField } from '@src/shared/field.enum';
import { FIELDS } from '@src/shared/fields.constant';
import { LINKS } from '@src/shared/links.constant';
import React from 'react';
import { css } from 'twin.macro';
import { IAddressFormProps } from './check-delivery.component';

interface IProps {
    form: IUseCustomFormReturn<IAddressFormProps>;
    registerInfo: IRegisterModel;
    showPostCodeNotServed: boolean;
}

export const AddressCheckerForm = ({ form, registerInfo, showPostCodeNotServed }: IProps): JSX.Element => {
    return (
        <>
            <Form form={form}>
                <div tw="md:flex text-center">
                    <div tw="w-full" css={styles.select}>
                        <FieldLabelV2 label="Enter your address" />
                        <div tw="flex flex-col md:flex-row items-start gap-2.5 mt-2 mb-2.5">
                            <FormAddressByEnum
                                addressType={ENV.REACT_APP_ADDRESS_TYPE as EAddressType}
                                tw="w-full"
                                css={styles.postCodeInput}
                                name="shippingAddressSearch"
                                placeholder="Start typing your address or postcode"
                                {...FIELDS[EField.registrationCheckPostCodeLoqate]}
                                readOnly={!!registerInfo.stripe_payment_method_id}
                                validate={{
                                    validate: (val: IInputLoqateValue): string | undefined => {
                                        return !val.originalLabel ? CONST.thisFieldRequired : undefined;
                                    },
                                }}
                            />
                            <ButtonV2 tw="w-full md:w-auto h-12" name="submit" variant="primary" disabled={!form.isValid}>
                                Check address
                            </ButtonV2>
                        </div>
                    </div>
                </div>
            </Form>
            <div tw="mb-2 text-sm">
                <p>
                    <span css={styles.hint}>
                        <strong>Incorrect building or flat number?</strong> No problem, change it in the 'Your delivery' step.
                    </span>
                </p>
            </div>


            {showPostCodeNotServed && (
                <StatusTagV2
                    status="warning"
                    name="Postcode not served"
                    title="Sorry, we don't seem to deliver to your address yet"
                    showButton
                    onClick={(): void => {
                        window.open(LINKS.external_waiting_list.toString(), '_blank');
                    }}
                    buttonText="Join waiting list"
                    tw="w-full"
                >
                    Fear not, we are expanding all the time. If you'd like to be notified as soon as we deliver to your area,
                    please join our waiting list below. <strong>Please double check you entered your correct address</strong>
                    .
                </StatusTagV2>
            )}
        </>
    );
};

const styles = {
    postCodeInput: css`
        input {
            ${MQ.md} {
                min-width: 500px;
                max-width: 500px;
            }
        }
    `,
    select: css`
        && .ant-select-selector {
            height: 48px !important;
            border: 1px solid ${THEME.GRAY_300} !important;
            line-height: 24px;
            border-radius: 4px;
            padding: 12px !important;
            display: flex !important;
            align-items: center !important;
            width: 100%;
            overflow: hidden;
            :hover {
                border-color: ${THEME.TEXT_BLACK};
            }
        }

        && .ant-select-selection-item {
            font-weight: 300;
            text-overflow: ellipsis;
        }

        && .ant-select-selection-search {
            display: flex;
            align-items: center;
            padding: 12px 0;
        }

        && .ant-select-selection-placeholder {
            top: 9px;
        }

        && div.ant-select-focused.ant-select-show-search .ant-select-selector {
            box-shadow: none !important;
        }
    `,
    hint: css`
        color: ${THEME.GRAY};
    `,
};
